<template>
  <v-container class="ma-0 pa-0" fluid>
    <v-toolbar flat color="toolbar">
      <v-col md="3" lg="3" xl="2" class="pl-0 pr-2">
        <v-autocomplete v-if="!isTprProRetailUser"
          v-model="selectedAdGroup"
          :items="userAdGroups"
          item-text="name"
          item-value="id"
          outlined
          return-object
          dense
          class="mt-7 ma-0"
          background-color="input"
          label="Ad Group"
          @change="onSelectionChange"
        ></v-autocomplete>
        <v-autocomplete v-else
          v-model="store"
          :items="userStores"
          background-color="input"
          class="mt-7 ma-0"
          dense
          item-text="display_name"
          item-id="store_party_id"
          label="Store Group / Store"
          :menu-props="{ maxHeight: 210 }"
          outlined
          return-object
          @change="onSelectionChange">
        </v-autocomplete>
      </v-col>
      <v-spacer></v-spacer>
      <v-col class="pr-6" align="end">
        <v-tooltip bottom>
          <template v-slot:activator="{ on, attrs }">
            <v-btn
              :disabled="disableRuleCreation"
              small
              class="blue white--text  ml-2"
              height="38"
              v-bind="attrs"
              v-on="on"
              @click="openRulesModal">
              create
            </v-btn>
          </template>
          <span>Create Rules</span>
        </v-tooltip>
      </v-col>
    </v-toolbar>

    <div v-if="isLoading" class="d-flex justify-center align-center" style="height: 300px;">
      <v-progress-circular indeterminate color="primary"></v-progress-circular>
    </div>

    <v-data-table 
      v-if="!isLoading"
      :headers="computedHeaders"
      :items="orderedRules"
      :items-per-page="-1"
      :no-data-text="noDataText"
      dense
      class="elevation-1"
      hide-default-footer
      group-by="priority">

      <template v-slot:[`group.header`]="{group, headers, toggle, isOpen, items}">
        <td :colspan="headers.length" class="ruleNames font-weight-bold">
          {{ group + ". " + items[0].rule_display_name }}
        </td>
      </template>

      <template v-slot:[`item.perishable`]="{ item }">
        <span>{{ formatBatchType(item.perishable) }}</span>
      </template>

      <template v-slot:[`item.perishable`]="{ item }">
        <v-chip v-for="(type) in getBatchTypes(item.perishable)"
          class="mr-2"
          :key="type"
          small
          :color="type == 'Perishable' ? 'amber darken-3' : 'primary'"
          text-color="white">
          <span 
            style="
              width: 100px;
              text-align: center;">
            {{type}}
          </span>
        </v-chip>
      </template>

      <template v-slot:[`item.rule_value`]="{ item }">
        <span 
          v-if="item.label != 'None'">
          {{ formatRuleValue(item.rule_value, item.label) }}
        </span>
        <v-icon v-else color="green">mdi-arrow-up-circle</v-icon>
      </template>

      <template v-slot:[`item.filter_lvl`]="{ item }">
        <span v-if="item.filter_lvl">{{ getRuleDisplay(item.filter_lvl) }}</span>
        <v-icon v-else color="red">mdi-close-circle</v-icon>
      </template>

      <template v-slot:[`item.filter_value`]="{ item }">
        <span v-if="item.filter_value">{{ item.filter_value }}</span>
        <v-icon v-else color="red">mdi-close-circle</v-icon>
      </template>

      <template v-slot:[`item.actions`]="{ item }">
        <v-menu offset-y>
          <template #activator="{ on }">
            <v-btn icon v-on="on">
              <v-icon>mdi-pencil-box-outline</v-icon>
            </v-btn>
          </template>
          <v-list>
            <v-list-item v-if="isTprProRetailUser" @click="openShuttleModal(item)">
              <v-list-item-icon>
                <v-icon>mdi-truck-delivery-outline</v-icon>
              </v-list-item-icon>
              <v-list-item-title>Shuttle</v-list-item-title>
            </v-list-item>
            <v-list-item @click="editRule(item)">
              <v-list-item-icon>
                <v-icon>mdi-pencil</v-icon>
              </v-list-item-icon>
              <v-list-item-title>Edit</v-list-item-title>
            </v-list-item>
            <v-list-item @click="deleteRule(item)">
              <v-list-item-icon>
                <v-icon color="red">mdi-trash-can</v-icon>
              </v-list-item-icon>
              <v-list-item-title>Delete</v-list-item-title>
            </v-list-item>
          </v-list>
        </v-menu>
      </template>
    </v-data-table>
    <v-dialog
      v-model="isRulesModalOpen" 
      :max-width="isDefinitionsVisible ? '1000px' : '500px'"
      @click:outside="closeRulesModal">
      <v-form @submit.prevent v-model="valid">
        <v-card>
          <v-card-title class="main white--text pa-2 d-flex justify-space-between">
            <span> Rule Manager </span>
            <v-icon
              class="ml-auto"
              color="grey lighten-1"
              @click="toggleDefinitions"
            >mdi-information-outline</v-icon>
          </v-card-title>

          <v-card-text class="pa-3">
            <v-row>
              <v-col :cols="isDefinitionsVisible ? 6 : 12" class="pr-3" style="border-right: 1px solid #e0e0e0;">
                <v-stepper v-model="step" vertical linear class="mb-5">
                  
                  <!-- Step 1: Rule Selection -->
                  <v-stepper-step :complete="isRuleSelected" :editable="isRuleSelected || !!editingRuleId" step="1">
                    Select Rule <span v-if="selectedRule.rule_display_name" class="displayedValues"> {{ selectedRule.rule_display_name }}</span>
                  </v-stepper-step>
                  <v-stepper-content step="1">
                    <v-row dense>
                      <v-col cols="8">
                        <v-select
                          class="pt-1"
                          v-model="selectedRule.rule_display_name"
                          :items="ruleOptions"
                          label="Select Rule"
                          item-text="display_name"
                          item-value="display_name"
                          outlined
                          :rules="ruleSelect"
                          dense
                          @change="setInitialValue"
                        ></v-select>
                      </v-col>
                    </v-row>
                  </v-stepper-content>

                  <!-- Step 2: Rule Value Input -->
                  <v-stepper-step :complete="isValueEntered" :editable="isRuleSelected || !!editingRuleId" step="2">
                    Enter Value <span v-if="selectedRule.rule_value" class="displayedValues"> {{ selectedRule.rule_value }}</span>
                  </v-stepper-step>
                  <v-stepper-content step="2">
                    <v-row dense>
                      <v-col cols="6">
                        <v-text-field v-if="selectedRule.label === 'Number' || selectedRule.label === 'Percentage'"
                          class="pt-1"
                          v-model="selectedRule.rule_value"
                          label="Value"
                          outlined
                          placeholder="Enter value"
                          :rules="getRuleValueRules()"
                          dense
                          type="number"
                          :step="selectedRule.rule_name === 'minimum_discount' ? 0.1 : 1"
                          :min="0"
                          @change="handleValueInput"
                          @keydown.enter="handleValueInput"
                        >
                          <template v-slot:prepend-inner>
                            <span v-if="selectedRule.label === 'Number'">$</span>
                            <span v-if="selectedRule.label === 'Percentage'">%</span>
                          </template>
                        </v-text-field>
                      </v-col>
                    </v-row>
                  </v-stepper-content>

                  <!-- Step 3: Batch Type Selection -->
                  <v-stepper-step :complete="isBatchTypeComplete" :editable="isValueEntered || !!editingRuleId" step="3">
                    Batch Type (Optional)
                  <span v-if="selectedRule.perishable != null" class="displayedValues"> {{ selectedRule.perishable == true ? 'Perishable' : 'Non Perishable' }}</span>
                  </v-stepper-step>
                  <v-stepper-content step="3">
                    <v-row>
                      <v-col cols="8">
                        <v-select
                          class="pt-1"
                          v-model="selectedRule.perishable"
                          :items="batchTypes"
                          item-text="name"
                          item-value="value"
                          label="Select Batch Type"
                          outlined
                          clearable
                          dense
                          @change="handleBatchTypeInput"
                        >
                        </v-select>
                      </v-col>
                    </v-row>
                    <!-- Skip Step 3 if no selection is made -->
                    <v-btn @click="skipBatchTypeStep" color="primary" small class="mt-3">Skip</v-btn>
                  </v-stepper-content>

                  <!-- Step 4: Filter Selection -->
                  <v-stepper-step :complete="isFilterComplete" :editable="isValueEntered || !!editingRuleId" step="4">
                    Filters (Optional)
                  <span v-if="displayFilterText" class="displayedValues"> {{ displayFilterText }}</span>
                  </v-stepper-step>
                  <v-stepper-content step="4">
                    <v-row>
                      <v-col cols="8">
                        <v-select
                          class="pt-1"
                          v-model="selectedDepartment"
                          :items="filteredDepartments"
                          label="Select Department"
                          outlined
                          clearable
                          dense
                          @change="onDepartmentChange"
                        ></v-select>
                      </v-col>
                    </v-row>
                    <v-row>
                      <v-col cols="8">
                        <v-select
                          class="pt-1"
                          v-model="selectedCategory"
                          :items="filteredCategories"
                          label="Select Category"
                          outlined
                          clearable
                          dense
                          @change="onCategoryChange"
                        ></v-select>
                      </v-col>
                    </v-row>
                    <!-- Skip Step 4 if no selection is made -->
                    <v-btn @click="skipFilterStep" color="primary" small class="mt-3">Skip</v-btn>
                  </v-stepper-content>

                  <!-- step 5 -->
                  <v-stepper-step :complete="isRulesComplete" :editable="isFilterComplete" step="5">
                    Complete
                  </v-stepper-step>
                  <v-stepper-content step="5">
                    <v-row dense>
                      <v-col cols="2" class="pt-1">
                        <v-btn @click="completeRuleset" color="primary" class="mb-4" small>Complete</v-btn>
                      </v-col>
                    </v-row>
                  </v-stepper-content>
                </v-stepper> 
              </v-col>

              <v-col v-if="isDefinitionsVisible" cols="6" class="pl-3">
                <h3 class="definitions-title" style="border-bottom: 1px solid #e0e0e0;">Definitions</h3>
                  <div
                    v-for="(definition, index) in definitions"
                    :key="index"
                    class="definition-term"
                  >
                  {{ index + 1 }}. <strong>{{ definition.term }}</strong>
                  <ul class="definition-list">
                    <li v-for="condition in formatDescription(definition.description)" :key="condition">{{ condition }}</li>
                  </ul>
                </div>
              </v-col>
            </v-row>
          </v-card-text>
        </v-card>
      </v-form>
    </v-dialog>
    <RulesetShuttle
      :value.sync="isShuttleModalOpen"
      :ruleset="shuttleRuleset"
      @shuttled="getRulesets"
    />
  </v-container>
</template>
<script>
import { fullwidth } from "@/mixins/fullwidth"
import { paging } from "@/mixins/paging"
import { displayHelpers } from "@/mixins/displayHelpers"
import { userAccess } from "@/mixins/user-access"
import { utils } from "@/mixins/utils"
import { shared } from "@/mixins/shared"
import Rules from "@/axios/rules-management"
import RulesetShuttle from "@/components/RulesetShuttle.vue"

const PERISHABLE_DEPARTMENTS = [
  'Bakery',
  'Deli',
  'Meat - Fresh',
  'Meat - Frozen',
  'Meat - Processed',
  'Meat - Seafood',
  'Produce'
]

export default {
  name: "TprPro",
  components: {
    RulesetShuttle
  },
  mixins: [
    fullwidth,
    displayHelpers,
    paging,
    userAccess,
    utils,
    shared
  ],
  data() {
    return {
      selectedAdGroup: {},
      store: {},
      userStores: [],
      step: 1,
      level: '',
      filter_lvl: '',
      filter_value: '',
      filterStepperOpen: false,
      selectedFilterLvl: null,
      departmentOptions: [],
      departmentCategoryPairs: [],
      selectedDepartment: null,
      selectedCategory: null,
      batchTypes: [
        { name: "PERISHABLE", value: true },
        { name: "NON PERISHABLE", value: false }
      ],
      selectedRule: {},
      ruleOptions: [],
      ruleSelect: [
        value => {
          if (value) return true
          return 'Rule selection is required'
        }
      ],
      rulesData: [],
      relationships: [],
      isRulesModalOpen: false,
      isRuleSelected: false,
      isValueEntered: false, 
      valid: false,
      editingRuleId: null,
      isFilterComplete: false,
      isBatchTypeComplete: false,
      isRulesComplete: false,
      definitions: [
        { term: "Rounding down if < 5 up if > 5", description: "If last digit is 0, subtract .01. If last digit > 5, move up to 9. If last digit < 5 move down to 9" },
        { term: "Rounding up to 5 or 9", description: "If last digit < 5 set to 5. If last digit 6 to 8 set to 9" },
        { term: "Rounding up to 7 or 9", description: "If last digit < 7 set to 7. If last digit is 8 set to 9" },
        { term: "Rounding up to 5 or 9 minus 1 on 0", description: "If last digit is 0, subtract .01. If 1 through 5, set to 5. If 6 through 8, set to 9" },
        { term: "Rounding up to 9", description: "If last digit is 0 through 8, set up to 9." },
        { term: "Rounding up to nearest odd number minus 1 on 0", description: "If last digit is 0, subtract .01. If 1 through 2, move up to 3. If 4, set to 5. If 6, set to 7. If 8, set to 9" },

      ],
      isDefinitionsVisible: false,
      isLoading: false,
      isShuttleModalOpen: false,
      shuttleRuleset: {}
    }
  },
  mounted() {
    this.initializeData()
  },
  watch: {
    userAdGroups: {
      handler(newValue) {
        if (newValue?.length > 0 && !sessionStorage.tpr_pro_ad_group) {
          let index = 0
          this.selectedAdGroup = newValue[index]
        }
      },
      deep: true
    },
    store(newValue) {
      if (newValue){
        this.saveState()
        // this.getRulesets()
      }
    },
    selectedAdGroup(newValue){
      if(newValue) {
        this.saveState()
        // this.getRulesets()
      }
    },
    async viewMode() {
      if (this.$auth.tenant !== 'alliance-retail-group') return
      if (this.isTprProRetailUser) {
        if (this.userStores.length === 0) await this.getStores()
        if (this.userStores.length === 0) {
          this.alert('You are not associated with any TPR Pro stores. Please contact support for access.')
          this.$router.replace('/')
          return
        }
        if (!this.store?.store_party_id) this.store = this.userStores[0]
      } else if (!this.selectedAdGroup?.id && this.userAdGroups.length > 0) {
        this.selectedAdGroup = this.userAdGroups[0]
      }
      this.onSelectionChange()
    },
  },
  computed: {
    isTprProRetailUser() {
      if (this.adminUser) return this.viewMode === 'STORE'
      return this.userGroups.includes('tpr-manager.pro')
    },
    disableRuleCreation() {
      if (this.isTprProRetailUser)
        return this.userStores.length === 0 || !this.store || !this.store.store_party_id
      else
        return this.userAdGroups.length == 0
    },
    orderedRules() {
      if (this.rulesData.length == 0)
        return []

      let priority_rankings = this.rulesData.reduce((acc, rule) => {
        const groupName = rule.rule_display_name
        if (!acc.find(item => item.name === groupName)) {
          acc.push({"name": groupName, "priority": rule.rule_priority})
        }
        return acc
      }, [])
      priority_rankings = priority_rankings.sort((a, b) => a.priority - b.priority)

      return this.rulesData.map(item => ({
        ...item,
        priority: priority_rankings.findIndex(rule => rule.name === item.rule_display_name) + 1
      }))
    },
    computedHeaders() {
      return [
        { text: "Rule", value: "rule_display_name", sortable: false, align: "start", class: 'accent white--text' },
        { text: "Batch Type", value: "perishable", sortable: false, align: "start", class: 'accent white--text' },
        { text: "Rule Value", value: "rule_value", sortable: false, align: "start", class: 'accent white--text' },
        { text: "Rule Filter", value: "filter_lvl", sortable: false, align: "start", class: 'accent white--text' },
        { text: "Rule Filter Value", value: "filter_value", sortable: false, align: "start", class: 'accent white--text' },
        { text: "Actions", value: "actions", sortable: false, align: "start", class: 'accent white--text' }
      ]
    },
    displayFilterText() {
      return this.filter_lvl && this.filter_value
        ? `${this.filter_lvl} - ${this.filter_value}`
        : ''
    },
    filteredDepartments() {
      const perishable = this.selectedRule.perishable
      if (perishable === true) {
        return this.departmentOptions.filter(d => PERISHABLE_DEPARTMENTS.includes(d))
      }
      if (perishable === false) {
        return this.departmentOptions.filter(d => !PERISHABLE_DEPARTMENTS.includes(d))
      }
      return this.departmentOptions
    },
    filteredCategories() {
      return [...new Set(
        this.departmentCategoryPairs
          .filter(p => !this.selectedDepartment || p.column === this.selectedDepartment)
          .map(p => p.downstream_column)
      )]
    },
    noDataText() {
      if (this.isTprProRetailUser) {
        if (this.userStores.length === 0) {
          return "No TPR Pro stores available"
        }
        if (!this.store || !this.store.store_party_id) {
          return "No store selected"
        }
        return "No data available"
      }

      if (!this.isTprProRetailUser && this.disableRuleCreation) {
        return "No Ad Group selected"
      }

      return "No data available"
    }
  },
  methods: {
    flattenStoreParties(storeParties) {
      const isPriceProStore = store => {
        const flag = store?.is_price_pro_store
        return typeof flag === 'string' ? flag.toLowerCase() === 'true' : Boolean(flag)
      }

      const childStoreItems = (Array.isArray(storeParties) ? storeParties : [])
        .filter(p => p.party_type === 'TPR_STORE_GROUP')
        .flatMap(group => group.children || [])
        .filter(store => isPriceProStore(store))
        .map(store => ({
          ...store,
          display_name: store.store_name
        }))

      const topLevelStoreItems = (Array.isArray(storeParties) ? storeParties : [])
        .filter(store => store.party_type === 'STORE' && isPriceProStore(store))
        .map(store => ({
          ...store,
          display_name: store.store_name
        }))
      
      const storeItems = [...childStoreItems, ...topLevelStoreItems]
        .filter((store, index, stores) =>
          index === stores.findIndex(candidate => candidate.store_party_id === store.store_party_id)
        )
        .sort((a, b) => a.store_name.localeCompare(b.store_name))

      return storeItems
    },
    formatDescription(description) {
      return description.split(/(?=If\s)/g).map(item => item.trim())
    },
    formatRuleValue(value, type) {
      if (type === "Percentage") {
        return `${value}%`
      } else if (type === "Number") {
        return `$${value}`
      }
    },
    getBatchTypes(value) {
      if (value == null) {
        return ["Perishable", "Non Perishable"]
      } else if (value === true) {
        return ["Perishable"]
      } else return ["Non Perishable"]
    },
    toggleDefinitions() {
      this.isDefinitionsVisible = !this.isDefinitionsVisible
    }, 
    getRuleValueRules() {
      const rule = this.selectedRule
      let rules = []

      if (rule.label === 'None' || rule.label === 'rounding') {
        return []
      }

      rules.push(value => {
        if (!value || isNaN(value)) {
          return 'Value must be a valid number'
        }
        return true
      })

      rules.push(value => {
        if (typeof value === 'string' && value.includes('.')) {
          const decimalPlaces = value.split('.')[1].length
          if (decimalPlaces > 2) {
            return 'Only up to 2 decimal places allowed'
          }
        }
        return true
      })

      if (rule.label === 'Percentage' && rule.rule_name === 'pass_thru_pct') {
        rules.push(value => {
          const numericValue = Number(value)
          if (numericValue < 70 || numericValue > 100) {
            return 'Value must be between 70%-100%'
          }
          return true
        })
      } else if (rule.label === 'Percentage' && rule.rule_name !== 'pass_thru_pct') {
        rules.push(value => {
          const numericValue = Number(value)
          if (numericValue < 0 || numericValue > 100) {
            return 'Value must be between 0-100%'
          }
          return true
        })
      } else if (rule.label === 'Number') {
        rules.push(value => {
          const numericValue = Number(value)
          if (numericValue < 0) {
            return 'Value must be greater than 0'
          }
          return true
        })
      }

      return rules
    },
    saveState() {
      if (!this.isTprProRetailUser) {
        if (this.selectedAdGroup) {
          sessionStorage.setItem("tpr_pro_ad_group", JSON.stringify(this.selectedAdGroup))
        }
      } else {
        if (this.store) {
          sessionStorage.setItem("tpr_pro_selected_store", JSON.stringify(this.store))
        }
      }
    },

    onDepartmentChange() {
      this.selectedCategory = null
      this.syncCascadeFilter()
    },
    onCategoryChange() {
      if (this.selectedCategory && !this.selectedDepartment) {
        this.selectedDepartment = this.departmentCategoryPairs.find(p => p.downstream_column === this.selectedCategory)?.column ?? null
      }
      this.syncCascadeFilter()
    },
    syncCascadeFilter() {
      if (this.selectedCategory) {
        this.filter_lvl = 'category'
        this.filter_value = this.selectedCategory
      } else if (this.selectedDepartment) {
        this.filter_lvl = 'department'
        this.filter_value = this.selectedDepartment
      } else {
        this.filter_lvl = ''
        this.filter_value = ''
      }
      this.isFilterComplete = true
    },
    skipBatchTypeStep() {
      this.isBatchTypeComplete = true
      this.step = 4
    },
    skipFilterStep() {
      this.isFilterComplete = true
      this.step = 5
    },
    getRuleDisplay(key) {
      const ruleMapping = {
        'ad_group' : 'Ad Group',
        'store' : 'Store',
        'department' : 'Department',
        'category' : 'Category'
      }
      return ruleMapping[key] || key
    },
    formatDate(date) {
      const options = {month: '2-digit', day: '2-digit', year: 'numeric'}
      return new Date(date).toLocaleDateString('en-US', options)
    },
    openRulesModal() {
      this.isRulesModalOpen = true
      this.editingRuleId = null
      this.selectedRule = { rule_display_name: "", rule_value: "", rule_name: "", rule_priority: "", perishable: null }
      this.step = 1
      this.isFilterComplete = false
      this.isRulesComplete = false
      this.selectedFilterLvl = null
      this.selectedDepartment = null
      this.selectedCategory = null
    },
    closeRulesModal() {
      this.isRulesModalOpen = false
      this.selectedRule = {}
      this.description = ''
      this.step = 1
      this.isFilterComplete = false
      this.isRulesComplete = false
      this.selectedFilterLvl = null
      this.selectedDepartment = null
      this.selectedCategory = null
      this.isRuleSelected = false
      this.isValueEntered = false
      this.isBatchTypeComplete = false
    },

    setInitialValue() {
      const ruleDisplayName = this.selectedRule.rule_display_name
      const ruleOption = this.ruleOptions.find(option => option.display_name === ruleDisplayName)

      if (ruleOption) {
        this.selectedRule.rule_name = ruleOption.name
        this.selectedRule.label = ruleOption.label
        if(ruleOption.name === 'pass_thru_pct'){
          this.selectedRule.rule_value = 80
          this.isValueEntered = true
          this.isRuleSelected = true
          this.step = 2
        } 
         else if(ruleOption.name === 'minimum_discount'){
          this.selectedRule.rule_value = 0.10
          this.isValueEntered = true
          this.isRuleSelected = true
          this.step = 2
        }
         else if (ruleOption.label === 'None') {
          this.selectedRule.rule_value = ''
          this.isRuleSelected = true
          this.step = 3
        } else {
          this.selectedRule.rule_value = 0
          this.isValueEntered = true
          this.isRuleSelected = true
          this.step = 2
        }
      }
    },

    handleValueInput() {
      const rules = this.getRuleValueRules()
      const value = this.selectedRule.rule_value
      const isValid = rules.every(rule => rule(value) === true)
      
      if (isValid && value) {
        this.isValueEntered = true
        this.step = 3
      }
    },
    handleBatchTypeInput() {
      this.selectedDepartment = null
      this.selectedCategory = null
      this.filter_lvl = ''
      this.filter_value = ''
      this.isBatchTypeComplete = true
      this.step = 4
    },
    completeRuleset() {
      if (!this.valid) {
        return
      }
      this.createOrUpdateRulesets()
      this.closeRulesModal()
    },
    editRule(rule) {
      this.isRulesModalOpen = true
      this.editingRuleId = rule.ruleset_id
      this.step = 1
      
      this.isRuleSelected = true
      this.isValueEntered = true
      this.isFilterComplete = true 
      this.isRulesComplete = true
      this.isBatchTypeComplete = true
      
      this.selectedRule = {
        rule_display_name: rule.rule_display_name,
        rule_value: rule.label === 'None' ? '' : rule.rule_value,
        rule_priority: rule.rule_priority,
        rule_name: rule.rule_name,
        label: rule.label || 'None',
        created_date: rule.created_date,
        perishable: rule.perishable
      }
      this.filter_lvl = rule.filter_lvl || ''
      this.filter_value = rule.filter_value || ''

      this.prefillCascade(this.filter_lvl, this.filter_value)

    },
    prefillCascade(filterLvl, filterValue) {
      this.selectedDepartment = null
      this.selectedCategory = null
      if (filterLvl === 'department') {
        this.selectedDepartment = filterValue
      } else if (filterLvl === 'category') {
        this.selectedCategory = filterValue
        this.selectedDepartment = this.departmentCategoryPairs.find(p => p.downstream_column === filterValue)?.column ?? null
      }
    },
    async onSelectionChange() {
      this.isLoading = true
      try {
        this.rulesData = []
        this.saveState()
        await Promise.all([
          this.getRules(this.isTprProRetailUser),
          this.getRulesets()
        ])
      } catch (err) {
        console.error('Error during selection change:', err)
      } finally {
        this.isLoading = false
      }
    }, 
    createOrUpdateRulesets() {
      if (!this.valid) {
        return
      }
      const ruleData = {
        filter_lvl: this.filter_lvl || '',
        filter_value: this.filter_value || '',
        level: this.isTprProRetailUser ? 'store' : 'ad_group',
        party_id: this.isTprProRetailUser ? this.store.store_party_id : this.selectedAdGroup.id,
        name: this.isTprProRetailUser ? this.store.store_name : this.selectedAdGroup.name,
        modified_by: this.$auth.user.email,
        created_date: this.selectedRule?.created_date,
        rule_list: [{
          display_name: this.selectedRule?.rule_display_name,
          name: this.selectedRule?.rule_name,
          value: Number(this.selectedRule?.rule_value)
        }],
        perishable: this.selectedRule?.perishable
      }

      if (this.editingRuleId) {
        ruleData.ruleset_id = this.editingRuleId
        this.updateRulesets(ruleData)
      } else {
        ruleData.created_by = this.$auth.user.email
        this.createRulesets(ruleData)
      }
    },
    async initializeData() {
      try {
        this.isLoading = true
        if (!this.isTprProRetailUser) {
          const savedAdGroup = sessionStorage.getItem("tpr_pro_ad_group")
          this.selectedAdGroup = savedAdGroup ? JSON.parse(savedAdGroup) : this.userAdGroups[0]
        } else {
          const stores = await this.getStores()
          const savedStore = sessionStorage.getItem("tpr_pro_selected_store")
          const parsedStore = savedStore ? JSON.parse(savedStore) : null

          if (parsedStore && stores.some(store => store.store_party_id === parsedStore.store_party_id)) {
            this.store = parsedStore
          } else {
            this.store = stores.length > 0 ? stores[0] : null
          }

          if (!this.store) {
            this.alert('You are not associated with any TPR Pro stores. Please contact support for access.')
            this.isLoading = false
            this.$router.replace('/')
            return
          }
        }

        if (this.selectedAdGroup || this.store) {
          this.getRulesets()
          this.getRules(this.isTprProRetailUser)
          this.getPossibleValues()
        }
        this.saveState()

      } catch (err) {
        console.error("Error initializing data:", err)
      } finally {
        this.isLoading = false
      }
    },
    async getPossibleValues() {
      try {
        const deptCats = await Rules.getPossibleValues('department', 'category')
        this.departmentCategoryPairs = deptCats.data
        this.departmentOptions = [...new Set(deptCats.data.map(p => p.column))]
      } catch (err) {
        console.error('Get possible values error: ', err)
      }
    },
    async getRules() {
      try {
        const res = await Rules.getRules(this.isTprProRetailUser)
        this.ruleOptions = res.data.sort((a, b) => a.priority - b.priority)
      } catch (err) {
        console.error("Error fetching rules:", err)
      }
    },
    async getStores(){
      try{
        const storeParties = await this.$store.dispatch('getUserProfileTprStoreGroups')
        this.userStores = this.flattenStoreParties(storeParties)
        return this.userStores
      } catch (err) {
        console.log('Get stores Error: ', err)
        this.userStores = []
        return []
      }
    },
    async getRulesets() {
      try {
        let matchingRulesets = []

        if (this.isTprProRetailUser) {
          if (this.userStores.length === 0) {
            this.alert("No Stores found for user, cannot retrieve rule sets.")
            return
          } else {
            if (!this.store || !this.store.store_party_id) {
              this.alert('Select a TPR Pro store to view rule sets.')
              return
            }
            const res = await Rules.getRulesets(this.store.store_party_id)
            matchingRulesets = res.data
          }
        } else {
          if (this.selectedAdGroup == null || this.selectedAdGroup.id == null) {
            this.alert("No Ad Groups found for user, cannot retrieve rule sets.")
            return
          } else {
            const res = await Rules.getRulesets(this.selectedAdGroup.id)
            matchingRulesets = res.data
          }
        }

        this.rulesData = matchingRulesets.map((ruleset) => ({
          ...ruleset,
          formated_created_date: this.formatDate(ruleset.created_date),
          formated_modified_date: this.formatDate(ruleset.modified_date),
        }))
      } catch (err) {
        console.error("Error fetching rulesets:", err)
      }
    },
    async createRulesets(ruleData) {
      try {
        this.isLoading = true
        await Rules.createRulesets(ruleData)
        this.closeRulesModal()
        await this.getRulesets()
      } catch (err) {
        if (err.response && err.response.status === 406) {
          const errorMessage = err.response.data?.message || 'An error occurred'
          const minValue = err.response.data?.extras?.value?.min_value
          const snackbarText = minValue === 100 ? errorMessage : `${errorMessage} Minimum value allowed: ${minValue}`

          this.$store.dispatch('setSnackbar', {
            status: 'error',
            text: snackbarText
          })
        } else {
          console.error('Error creating ruleset:', err)
        }
      } finally {
        this.isLoading = false
      }
    },
    async updateRulesets(ruleData) {
      try {
        this.isLoading = true
        await Rules.updateRulesets(ruleData)
        this.closeRulesModal()
        await this.getRulesets()
      } catch (err) {
        console.error('Error updating ruleset:', err)
        if (err.response && err.response.status === 406) {
          const errorMessage = err.response.data?.message || 'An error occurred'
          const minValue = err.response.data?.extras?.value?.min_value
          const snackbarText = minValue === 100 ? errorMessage : `${errorMessage} Minimum value allowed: ${minValue}`

          this.$store.dispatch('setSnackbar', {
            status: 'error',
            text: snackbarText
          })
        } else {
          console.error('Error creating ruleset:', err)
        }
      } finally {
        this.isLoading = false
      }
    },
    async deleteRule(rule) {
      try {
        await Rules.deleteRulesets(rule.ruleset_id)
        await this.getRulesets()
      } catch (err) {
        console.error("Error deleting rule:", err)
      }
    },
    openShuttleModal(rule) {
      this.shuttleRuleset = rule
      this.isShuttleModalOpen = true
    }
  }
}
</script>
<style scoped>
.displayedValues {
  font-weight: bolder;
  font-style: italic;
  padding-left: 10px;
}
.definitions-title {
  text-align: center;
}

.definition-list {
  display: none;
  margin-left: 20px;
}
.definition-term {
  padding: 5px;
}

.definition-term:hover .definition-list {
  display: block;
}
.theme--light.v-data-table
  .v-data-table-header
  th.sortable
  .v-data-table-header__icon {
  color: #fff !important
}
.v-card-text {
  padding: 8px 16px
}
.v-card-actions {
  padding: 8px 16px
}
.ruleNames{
  background-color: rgb(225, 222, 222) !important
}
</style>
